import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { meetingsApi, type MeetingDetail } from '@/lib/meetings';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ListeningOrb } from '@/components/live/ListeningOrb';
import { SuggestionCard, type SuggestionItem } from '@/components/live/SuggestionCard';
import { TranscriptStream, type TranscriptItem } from '@/components/live/TranscriptStream';
import { useAudioCapture } from '@/hooks/useAudioCapture';
import { useVideoCapture } from '@/hooks/useVideoCapture';
import { useLiveSession } from '@/hooks/useLiveSession';
import { useWakeLock } from '@/hooks/useWakeLock';
import { useMediaUpload } from '@/hooks/useMediaUpload';

type Phase = 'prep' | 'connecting' | 'live' | 'ending' | 'ended' | 'error';

export function LiveSession() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [meeting, setMeeting] = useState<MeetingDetail | null>(null);
  const [phase, setPhase] = useState<Phase>('prep');
  const phaseRef = useRef<Phase>('prep');
  phaseRef.current = phase;

  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [permsError, setPermsError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);

  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([]);
  const [transcripts, setTranscripts] = useState<TranscriptItem[]>([]);
  const partialIdRef = useRef<string | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const startTimeRef = useRef<number>(0);

  const live = useLiveSession(id || '');
  const liveRef = useRef(live);
  liveRef.current = live;

  const audio = useAudioCapture(useCallback((buf: ArrayBuffer) => {
    liveRef.current.sendBinary(buf);
  }, []));
  const upload = useMediaUpload();
  useWakeLock(phase === 'live');

  useVideoCapture(
    videoRef,
    phase === 'live',
    useCallback((b64: string) => liveRef.current.sendJson({ type: 'video', data: b64 }), []),
  );

  // Attach the captured MediaStream to the <video> element whenever both
  // exist (covers both "video mounted before stream" and the reverse).
  useEffect(() => {
    if (!videoRef.current) return;
    if (streamRef.current && videoRef.current.srcObject !== streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [phase]);

  useEffect(() => {
    if (!id) return;
    meetingsApi.get(id).then(setMeeting).catch(() => setErrorMsg('Не удалось загрузить встречу'));
  }, [id]);

  useEffect(() => {
    if (phase !== 'live') return;
    const t = window.setInterval(() => {
      setElapsed(Date.now() - startTimeRef.current);
    }, 500);
    return () => window.clearInterval(t);
  }, [phase]);

  // Subscribe ONCE to live events; use refs inside to access current state.
  useEffect(() => {
    return live.onEvent((ev) => {
      if (ev.type === 'ready') {
        startTimeRef.current = Date.now();
        setPhase('live');
        const stream = streamRef.current;
        if (stream) {
          void audio.start(stream);
          if (id) void upload.start(stream, id);
        }
      } else if (ev.type === 'error') {
        if (phaseRef.current !== 'ending') {
          setErrorMsg(ev.message);
          setPhase('error');
        }
      } else if (ev.type === 'end') {
        // Don't override 'ending' — stopLive will set 'ended' after cleanup.
        if (phaseRef.current !== 'ending') {
          setPhase('ended');
        }
      } else if (ev.type === 'transcript') {
        setTranscripts((arr) => {
          if (!ev.isFinal) {
            const partialId = partialIdRef.current;
            const existing = partialId ? arr.find((t) => t.id === partialId) : undefined;
            const next: TranscriptItem = {
              id: existing?.id ?? `t_partial_${ev.ts}`,
              speaker: ev.speaker,
              lang: ev.lang,
              text: ev.text,
              ts: ev.ts,
              isFinal: false,
            };
            partialIdRef.current = next.id;
            const others = existing ? arr.filter((t) => t.id !== existing.id) : arr;
            return [...others, next];
          }
          // final — replace any partial of the same start_ms
          partialIdRef.current = null;
          const finalId = `t_${ev.ts}_${arr.length}`;
          return [
            ...arr.filter((t) => t.isFinal || t.ts !== ev.ts),
            {
              id: finalId,
              speaker: ev.speaker,
              lang: ev.lang,
              text: ev.text,
              ts: ev.ts,
              isFinal: true,
            },
          ];
        });
      } else if (ev.type === 'suggestion') {
        setSuggestions((arr) => [
          ...arr,
          {
            id: `s_${ev.ts}_${arr.length}`,
            kind: ev.kind,
            title: ev.title,
            body: ev.body,
            trigger: ev.trigger,
            ts: ev.ts,
          },
        ]);
        try { (navigator as any).vibrate?.(15); } catch {}
      }
    });
    // We intentionally only subscribe once on mount — `live.onEvent` is
    // stable (useCallback with []) and we use refs for currents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup on unmount: ensure mic/cam released even if user nav-aways mid-session.
  useEffect(() => {
    return () => {
      try { liveRef.current.close(); } catch {}
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, []);

  const startLive = async () => {
    setPermsError(null);
    setPhase('connecting');
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
        video: {
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 },
          frameRate: { ideal: 24 },
        },
      });
    } catch (err: any) {
      const msg = err?.name === 'NotAllowedError'
        ? 'Доступ к камере и микрофону запрещён. Разрешите в настройках браузера.'
        : err?.message || 'Не удалось получить доступ к камере и микрофону';
      setPermsError(msg);
      setPhase('prep');
      return;
    }
    streamRef.current = stream;
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(() => {});
    }
    live.connect();
  };

  const stopLive = useCallback(async () => {
    if (phaseRef.current === 'ended' || phaseRef.current === 'ending') return;
    setPhase('ending');
    try { live.stop(); } catch {}
    try { await audio.stop(); } catch {}
    try { await upload.stop(); } catch {}
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    try { live.close(); } catch {}
    setPhase('ended');
  }, [audio, upload, live]);

  const liveSuggestions = suggestions.slice(-4).reverse();

  if (phase === 'prep' || phase === 'connecting') {
    return (
      <div className="min-h-dvh flex flex-col px-5 pt-5 pb-8">
        <header className="flex items-center justify-between mb-4">
          <button onClick={() => nav(-1)} className="btn-ghost gap-1 -ml-2">
            ← Назад
          </button>
          <h1 className="text-[15px] font-semibold truncate max-w-[60%]">
            {meeting?.title ?? 'Встреча'}
          </h1>
          <span className="size-8" />
        </header>

        <div className="flex-1 grid place-items-center">
          <div className="flex flex-col items-center gap-6 max-w-md text-center">
            <ListeningOrb level={0} status="connecting" />
            <div>
              <h2 className="text-[20px] font-semibold">Готов слушать</h2>
              <p className="text-sm text-ink-2 mt-1">
                Нажми «Начать», разреши камеру и микрофон. AI начнёт писать
                подсказки прямо здесь, как только услышит первую фразу.
              </p>
            </div>
            {permsError && (
              <Card className="text-sm text-danger">{permsError}</Card>
            )}
            <Button size="lg" onClick={startLive} disabled={phase === 'connecting'}>
              {phase === 'connecting' ? 'Соединяю…' : 'Начать встречу'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'ending') {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center px-6 text-center gap-4">
        <ListeningOrb level={0} status="paused" size={140} />
        <div className="text-[18px] font-semibold">Сохраняю запись…</div>
        <div className="text-sm text-ink-2">
          Записанное аудио и видео загружаются на сервер.
          {upload.bytesSent > 0 && (
            <div className="text-xs text-ink-3 mt-1">
              Загружено: {(upload.bytesSent / 1024 / 1024).toFixed(1)} МБ
            </div>
          )}
        </div>
      </div>
    );
  }

  if (phase === 'ended' || phase === 'error') {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center px-6 text-center gap-4">
        <Card className="max-w-md">
          <div className="text-[13px] uppercase tracking-wider text-ink-3">
            Встреча завершена
          </div>
          <div className="mt-2 text-[18px] font-semibold">
            {phase === 'error' ? 'Соединение оборвалось' : 'Готовлю конспект'}
          </div>
          {errorMsg && <p className="text-sm text-danger mt-2">{errorMsg}</p>}
          <p className="text-sm text-ink-2 mt-2">
            Транскрипт и подсказки уже сохранены. Конспект будет готов через
            минуту.
          </p>
          <Button full className="mt-5" onClick={() => nav(`/m/${id}`)}>
            Открыть конспект
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-dvh flex flex-col">
      <video
        ref={videoRef}
        muted
        playsInline
        autoPlay
        className="absolute inset-0 -z-10 size-full object-cover opacity-25 blur-xl"
      />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-bg-0/40 via-bg-0/80 to-bg-0" />

      <header className="px-5 pt-5 pb-3 flex items-center justify-between">
        <div className="chip">{formatTime(elapsed)}</div>
        <div className="flex items-center gap-2 text-[11px] text-ink-2">
          <span className="size-2 rounded-full bg-success animate-pulseSoft" />
          В эфире
        </div>
        <button
          onClick={stopLive}
          className="grid place-items-center size-10 rounded-full bg-danger/90 text-white shadow-glow active:scale-95"
          aria-label="Завершить встречу"
        >
          <StopIcon />
        </button>
      </header>

      <section className="px-5 grid place-items-center mt-2">
        <ListeningOrb level={audio.level} status="live" size={150} />
      </section>

      <section className="flex-1 px-3 mt-4">
        <div className="text-xs uppercase tracking-wider text-ink-3 px-2 mb-2">
          Подсказки
        </div>
        {liveSuggestions.length === 0 ? (
          <Card className="mx-2 text-center text-ink-3 py-6 text-sm">
            Слушаю встречу. Подсказки появятся, когда услышу что-то полезное.
          </Card>
        ) : (
          <ul className="space-y-2 px-2">
            <AnimatePresence initial={false}>
              {liveSuggestions.map((s) => (
                <li key={s.id}>
                  <SuggestionCard item={s} />
                </li>
              ))}
            </AnimatePresence>
          </ul>
        )}
      </section>

      <footer className="px-5 pt-3 pb-5 flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={() => setShowTranscript(true)}>
          Транскрипт ({transcripts.length})
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const txt = window.prompt('Заметка:');
            if (txt && txt.trim()) liveRef.current.sendJson({ type: 'note', text: txt.trim() });
          }}
        >
          + Заметка
        </Button>
      </footer>

      <AnimatePresence>
        {showTranscript && (
          <TranscriptSheet
            items={transcripts}
            onClose={() => setShowTranscript(false)}
            onNote={(t) => liveRef.current.sendJson({ type: 'note', text: t })}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function TranscriptSheet({
  items,
  onClose,
  onNote,
}: {
  items: TranscriptItem[];
  onClose: () => void;
  onNote: (t: string) => void;
}) {
  return (
    <motion.div
      key="sheet"
      initial={{ y: '100%' }}
      animate={{ y: 0 }}
      exit={{ y: '100%' }}
      transition={{ type: 'spring', stiffness: 320, damping: 32 }}
      className="fixed inset-0 z-40 flex flex-col"
    >
      <button className="flex-1 bg-black/60" onClick={onClose} aria-label="Закрыть" />
      <div className="bg-bg-1/95 backdrop-blur-xl rounded-t-3xl border-t border-white/10 flex flex-col h-[80vh]">
        <div className="grid place-items-center pt-2 pb-1">
          <span className="block w-10 h-1.5 rounded-full bg-white/20" />
        </div>
        <div className="px-4 pb-2 flex items-center justify-between">
          <h3 className="text-[15px] font-semibold">Транскрипт</h3>
          <button className="btn-ghost text-sm" onClick={onClose}>Закрыть</button>
        </div>
        <div className="flex-1 min-h-0">
          <TranscriptStream items={items} />
        </div>
        <NoteForm onSend={onNote} />
      </div>
    </motion.div>
  );
}

function NoteForm({ onSend }: { onSend: (t: string) => void }) {
  const [v, setV] = useState('');
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!v.trim()) return;
        onSend(v.trim());
        setV('');
      }}
      className="border-t border-white/10 p-3 flex gap-2 bg-bg-0/40"
    >
      <input
        className="input-base flex-1"
        placeholder="Своя заметка для конспекта…"
        value={v}
        onChange={(e) => setV(e.target.value)}
      />
      <Button type="submit" size="sm" disabled={!v.trim()}>
        Сохранить
      </Button>
    </form>
  );
}

function StopIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
      <rect x="3" y="3" width="10" height="10" rx="2" />
    </svg>
  );
}

function formatTime(ms: number) {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}
